
import { Box, Tab, Tabs, styled } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { TaskFilter, TaskStatus } from 'models/tasks';
import ProjectTaskGrid from './ProjectTaskGrid';
import { ProjectContext } from '../project-layout';
import { TraceVisit } from 'components/shared/audit';
import useTasksCount from 'hooks/tasks/useTasksCount';

const TaskFilterTabs = styled(Tabs)(({ theme }) => ({
    color: '#333333',
    '& .MuiTabs-indicator': {
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main,
        height: '1px',
        borderRadius: '4px',
    },
    '& .MuiTab-root.Mui-focusVisible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
    }
}));

const TaskFilterTab = styled(Tab)(({ theme }) => ({
    borderBottom: 1,
    textTransform: 'none',
    '&.Mui-selected': {
        borderBottom: 1,
        fontWeight: 600,
    },
    '&.Mui-focusVisible': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiTouchRipple-root': {
        display: 'none'
    }
}));

const ProjectMyTasks = () => {
    
    const project = useContext(ProjectContext);
    const [taskFilter, setTaskFilter] = useState<TaskFilter>('in-progress');
    const [total, loading, error, forceRefresh] = useTasksCount('on-hold', project?.id);
        
        useEffect(() => {
            forceRefresh();
        }, [taskFilter, project?.id]);
    
        const evalTextLabel = (text: string, statusToEval: string = 'On Hold') => {
            if (text.includes(statusToEval) && total > 0)
                return `${text} (${total})`;
            else
                return text;
        }

    return (
        <>
        <TraceVisit description='Project My Tasks Tab' data={{
                workItemId: project?.id
        }} />
            <Box sx={{
                display: 'inline-block',
                mb: '24px'
            }}>
                <TaskFilterTabs 
                    value={taskFilter} 
                    aria-label="Task status filter tabs"
                    onChange={(e, newValue) => setTaskFilter(newValue as TaskFilter)}
                    role="tablist"
                    TabIndicatorProps={{
                        'aria-hidden': 'true'
                    }}
                >
                    <TaskFilterTab
                        label='In Progress'
                        value='in-progress'
                        id="tab-in-progress"
                        aria-controls="tabpanel-in-progress"
                        role="tab"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTaskFilter('in-progress');
                            }
                        }}
                    />
                    <TaskFilterTab
                        label='Upcoming'
                        value='upcoming'
                        id="tab-upcoming"
                        aria-controls="tabpanel-upcoming"
                        role="tab"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTaskFilter('upcoming');
                            }
                        }}
                    />
                    <TaskFilterTab
                        label={evalTextLabel('On Hold')}
                        value='on-hold'
                        id="tab-on-hold"
                        aria-controls="tabpanel-on-hold"
                        role="tab"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTaskFilter('on-hold');
                            }
                        }}
                    />
                    <TaskFilterTab
                        label='Completed'
                        value='completed'
                        id="tab-completed"
                        aria-controls="tabpanel-completed"
                        role="tab"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTaskFilter('completed');
                            }
                        }}
                    />
                    <TaskFilterTab
                        label='All Tasks'
                        value='all'
                        id="tab-all"
                        aria-controls="tabpanel-all"
                        role="tab"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setTaskFilter('all');
                            }
                        }}
                    />
                </TaskFilterTabs>
                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: '-1px' }}></Box>
            </Box>

            <ProjectTaskGrid
                statusFilter={taskFilter}
                showUser={false}
                project={project}
            />
        </>
    )
};

export default ProjectMyTasks;